import { API_BASE_URL } from '@synergy/config';
import { Address, DocumentStatus, DocumentType, Media, SalesOrderStatus, UserRole } from '@synergy/models';
import { ArrayHelpers, FieldArray, Formik, FormikProps } from 'formik';
import { Box, ButtonBase, FormHelperText } from '@mui/material';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    PaloConfirmationDialog,
    PaloDescriptionModeToggle,
    PaloDetailView,
    PaloDocumentPreview,
    PaloFormSection,
    PaloHistory,
    PaloOverflowMenu,
    PaloRecordPager,
    PaloStatusPill,
    PaloStep,
    PaloTabs,
    paloCurrencySign,
} from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolSpinner, ScrollToFieldError, WithRouterProps, withRouter } from '@synergy/components';
import { ReactElement } from 'react';
import { connect } from 'react-redux';
import { getDocumentStatusTranslationId, hasRole } from '@synergy/utils';
import { resetUploads, setUploads } from '@synergy/data-access';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import urlJoin from 'url-join';

import { AppState } from '../../../store/models/app-state';
import { Contact } from '../../../common/models/contact';
import { ConvertOutcome } from '../../convert-receipt/models/convert-outcome';
import { ConverterReceiptModal } from '../../convert-receipt/converter-receipt-modal';
import { Customer } from '../../../common/models/customer';
import { DetailStream } from '../../activity-sidebar/details/detail-stream';
import { FinancialCalculations } from '../../../common/utils/financial-calculations';
import { PaloDirtyProvider, PaloDirtyReporter } from '../../../common/components/palo/palo-dirty-context';
import { PaloDocumentChain, PaloDocumentChainTabName } from '../../document-chain/palo-document-chain';
import { PaloDocumentChainCreate, PaloDocumentChainProvider } from '../../document-chain/palo-document-chain-context';
import { PaloDocumentNotices } from '../../../common/components/palo/palo-document-notices';
import { PaloDocumentTitle } from '../../../common/components/palo/palo-document-title';
import { PaloFlowAction, PaloSaveAction, paloDocumentPhase } from '../../detail-header/palo-main-action';
import { PaloRecordDock } from '../../../common/components/palo/palo-record-dock';
import { PaloSalesOrderApprovalNotice } from './palo-sales-order-approval-notice';
import { PaloSalesOrderAttachments } from './palo-sales-order-attachments';
import { PaloSalesOrderDetailCommon } from './palo-sales-order-detail-common';
import { PaloSalesOrderDetailPosition } from './palo-sales-order-detail-position';
import { PaloSalesOrderNotesWatcher } from './palo-sales-order-notes-watcher';
import { PaloTextControl } from '../../../common/components/palo/palo-text-control';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { ReceiptNoteType } from '../../../common/models/enums/receipt-note-type';
import { SalesOrder } from '../models/salesOrder';
import { SalesOrderDetailCtaHelper } from '../components/sales-order-detail-cta-helper';
import { SalesOrderDetailFormValues } from '../models/sales-order-detail-form-values';
import { SalesOrderPosition } from '../models/salesOrder-position';
import { SalesOrderTransition } from '../../convert-receipt/models/sales-order/salesOrdersTransition';
import { Settings } from '../../../common/models/settings';
import { Transition } from '../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../convert-receipt/models/internal/transition-target';
import { User } from '../../../common/models/user';
import { activitiesSelector } from '../../../common/selectors/activity-selectors';
import {
    createOrUpdateSalesOrder,
    deleteSalesOrder,
    fetchSalesOrder,
    resetSalesOrderState,
} from '../sales-order-actions';
import {
    fetchActivities,
    resetActivities,
    resetDocumentType,
    setDocumentType,
} from '../../../common/actions/activity-actions';
import { fetchReceiptInternalNotes, resetReceiptInternalNotes } from '../../../common/actions/internal-notes-actions';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { salesOrderDetailValidationSchema } from '../containers/sales-order-detail-validation-schema';
import {
    transitionSalesOrderIntoInquiries,
    transitionSalesOrderIntoPurchaseOrders,
} from '../../convert-receipt/transition-actions';

interface PaloSalesOrderDetailProps extends WithRouterProps {
    entity?: SalesOrder;
    settings: Settings;
    user: User;
    transitionsFromCount: number;
    activityCount: number;
    attachmentCount: number;
    createOrUpdateSalesOrder: (salesOrder: SalesOrder, salesOrderId?: number) => void;
    deleteSalesOrder: (id: number) => void;
    fetchSalesOrder: (id: number) => void;
    resetSalesOrderState: () => void;
    setUploads: (media: Media[]) => void;
    resetUploads: () => void;
    transitionSalesOrderIntoPurchaseOrders: (transitionObject: SalesOrderTransition) => Promise<ConvertOutcome>;
    transitionSalesOrderIntoInquiries: (transitionObject: SalesOrderTransition) => Promise<ConvertOutcome>;
    fetchActivities: (docType: DocumentType, docId: number) => void;
    resetActivities: () => void;
    fetchTransitions: (docType: DocumentType, docId: number) => void;
    resetTransitions: () => void;
    setDocumentType: (docType: DocumentType) => void;
    resetDocumentType: () => void;
    fetchReceiptInternalNotes: (receiptNoteType: ReceiptNoteType, receiptId: number) => void;
    resetReceiptInternalNotes: () => void;
}

/** Which page of the body is open — the order itself, or what it belongs to. */
type PaloSalesOrderTab = 'document' | 'chain';

interface PaloSalesOrderDetailState {
    tab: PaloSalesOrderTab;
    ctaCanComplete: boolean;
    unlocked: boolean;
    warningDialogVisible: boolean;
    convertReceiptVisible: boolean;
    previewVisible: boolean;
    paramsId?: number;
}

interface SelectedCustomer {
    customer: Customer | null;
    contact: Contact | null;
    address: Address | null;
}

class BasePaloSalesOrderDetail extends FormComponent<
    SalesOrderDetailFormValues,
    PaloSalesOrderDetailProps,
    PaloSalesOrderDetailState
> {
    private isNew = true;
    private resourceId?: number;
    private formikInstance: FormikProps<SalesOrderDetailFormValues> | null = null;
    private completeAndSave = false;
    private focusPosition = false;
    private selectedCustomer: SelectedCustomer = { address: null, contact: null, customer: null };
    private isLoading = false;

    public state: PaloSalesOrderDetailState = {
        tab: 'document',
        ctaCanComplete: false,
        unlocked: false,
        warningDialogVisible: false,
        convertReceiptVisible: false,
        previewVisible: false,
        paramsId: undefined,
    };

    private initialValues: SalesOrderDetailFormValues = {
        customerAccount: undefined,
        customerContact: undefined,
        responsibleContact: this.props.user.contact,
        internalNote: '',
        positions: [],
        generatesPurchaseOrder: false,
        dueDate: undefined,
        createdAt: new Date(),
    };

    private getNewPosition = () => {
        const emptyPositions: Partial<SalesOrderPosition> = {
            desiredDeliveryDate: new Date(),
            costCentre: '',
            deliveryAddress: undefined,
            isDesiredDeliveryDateFrom: false,
            customerContact: undefined,
            avisoContact: undefined,
        };

        const formikValues = this.formikInstance?.values;
        const positions = formikValues?.positions;
        let template: Partial<SalesOrderPosition> | undefined = positions?.[positions.length - 1];
        if (!template) {
            template = {
                customerContact: this.selectedCustomer.contact,
            } as Partial<SalesOrderPosition>;
        }

        return SalesOrderPosition.fromPlainObject({
            ...emptyPositions,
            ...template,
            deliveryAddress: null,
            items: [],
            id: undefined,
        });
    };

    private handleDelete = (id?: number) => async () => {
        if (id) {
            await this.props.deleteSalesOrder(id);
            this.props.navigate(-1);
        }
    };

    private handleManualSave = () => {
        this.completeAndSave = false;
        if (this.formikInstance) {
            this.formikInstance.submitForm();
        }
    };

    private handleSaveAndComplete = () => {
        this.completeAndSave = true;
        if (this.formikInstance) {
            this.formikInstance.submitForm();
        }
    };

    private handleSubmit = (formValue: SalesOrderDetailFormValues) => {
        const salesOrder = SalesOrder.fromPlainObject(formValue);
        if (this.completeAndSave) {
            salesOrder.status = DocumentStatus.Completed;
        }
        this.props.createOrUpdateSalesOrder(salesOrder, this.props.entity && this.props.entity.id);
    };

    private handleAddPosition = (helper: ArrayHelpers) => () => {
        this.focusPosition = true;
        helper.push(this.getNewPosition());
    };

    private updateInternalValuesByProps(props: PaloSalesOrderDetailProps) {
        const entity = props.entity;

        if (entity && this.formikInstance) {
            const definedOnEntity: Partial<SalesOrder> = {};

            Object.keys(entity)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .filter(key => !!(entity as any)[key])
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .forEach(key => ((definedOnEntity as any)[key] = (entity as any)[key]));

            // resetForm, not setValues: the loaded document becomes the base
            // Formik compares against. With setValues the form stayed different
            // from the empty defaults forever and the marker never went out.
            this.formikInstance.resetForm({
                values: {
                    ...this.initialValues,
                    ...definedOnEntity,
                },
            });
        }
    }

    private handleQuickCustomerSelected = (customer: Customer | null, contact?: Contact, address?: Address) => {
        if (!this.formikInstance) {
            return;
        }

        this.selectedCustomer = {
            customer,
            contact: contact || null,
            address: address || null,
        };

        this.formikInstance.setFieldValue('customerAccount', customer);
        this.formikInstance.setFieldValue('customerContact', contact || null);

        const salesOrderPositions: SalesOrderPosition[] = this.formikInstance.values.positions;
        salesOrderPositions.forEach((_, index) => {
            const prefix = `positions.${index}.`;
            if (this.formikInstance) {
                this.formikInstance.setFieldValue(
                    `${prefix}deliveryAddress`,
                    address && address.isDeliveryAddress ? address : null
                );
                this.formikInstance.setFieldValue(`${prefix}customerContact`, contact || null);
                this.formikInstance.setFieldValue(`${prefix}avisoContact`, null);
                if (address && address.costCentre) {
                    this.formikInstance.setFieldValue(`${prefix}costCentre`, address.costCentre);
                }
            }
        });
    };

    private handleCustomerRemove = (helper: ArrayHelpers) => (index: number) => {
        helper.remove(index);
    };

    private handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        TransitionTarget.PurchaseOrder === target
            ? this.props.transitionSalesOrderIntoPurchaseOrders(transitionObject as SalesOrderTransition)
            : this.props.transitionSalesOrderIntoInquiries(transitionObject as SalesOrderTransition);

    private handleCtaHelperChanged = (ctaCanComplete: boolean) => {
        this.setState({ ctaCanComplete });
    };

    private handleUnlockedChange = () => {
        this.setState(state => ({ unlocked: !state.unlocked }));
    };

    private handleWarningOpen = () => {
        this.setState({ warningDialogVisible: true });
    };

    private handleWarningClose = () => {
        this.setState({ warningDialogVisible: false });
    };

    private handleConfirmDelete = () => {
        this.setState({ warningDialogVisible: false });
        this.handleDelete(this.props.entity?.id)();
    };

    private handleConvertReceiptOpen = () => {
        this.setState({ convertReceiptVisible: true });
    };

    private handleConvertReceiptClose = () => {
        this.setState({ convertReceiptVisible: false });
    };

    public UNSAFE_componentWillMount() {
        this.props.resetActivities();
        this.props.resetTransitions();
        this.props.resetReceiptInternalNotes();
        this.props.setDocumentType(DocumentType.SalesOrder);
        if (this.props.params['id'] === 'new') {
            return;
        }
        this.isNew = false;
        this.isLoading = true;
        this.resourceId = +this.props.params['id'];
        if (this.resourceId) {
            this.props.fetchSalesOrder(this.resourceId);
        }
        this.updateInternalValuesByProps(this.props);
        if (this.props.entity) {
            this.props.setUploads(this.props.entity.media || []);
        }
    }

    public componentWillUnmount() {
        this.props.resetSalesOrderState();
        this.props.resetUploads();
        this.props.resetActivities();
        this.props.resetTransitions();
        this.props.resetDocumentType();
        this.props.resetReceiptInternalNotes();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PaloSalesOrderDetailProps) {
        const paramsId = +nextProps.params['id'];
        if (paramsId !== +this.props.params['id'] && !this.isNew) {
            const salesOrderId = +nextProps.params['id'];
            this.props.fetchSalesOrder(salesOrderId);
            this.props.fetchActivities(DocumentType.SalesOrder, salesOrderId);
            this.props.fetchTransitions(DocumentType.SalesOrder, salesOrderId);
            return;
        }

        // Only when the server sent the order anew: run on every change of
        // props, it reset the form — whatever was typed and not yet saved
        // went — and fetched the activities each time (#2259, as #2256).
        if (nextProps.entity && nextProps.entity !== this.props.entity) {
            if (this.isNew) {
                nextProps.navigate(
                    nextProps.location.pathname.replace(nextProps.params['id'], nextProps.entity.id.toString()),
                    { replace: true }
                );
                this.resourceId = nextProps.entity.id;
                this.isNew = false;
            }
            this.isLoading = false;
            this.updateInternalValuesByProps(nextProps);
            this.props.fetchActivities(DocumentType.SalesOrder, nextProps.entity.id);
            this.props.fetchTransitions(DocumentType.SalesOrder, nextProps.entity.id);
            this.props.fetchReceiptInternalNotes(ReceiptNoteType.SalesOrders, nextProps.entity.id);
            this.props.setUploads(nextProps.entity.media || []);
        }

        this.setState({ paramsId });
    }

    /**
     * The pages of the body. "Belege" is the tab the four other Belegmasken
     * carry: what a document belongs to is read rather than filled in, and
     * before the first save there is nothing to read (#2216).
     */
    private getTabs = (tab: PaloSalesOrderTab, dirty: boolean): PaloStep[] => [
        {
            label: <FormattedMessage id="app.salesOrders.detail.step.overview" />,
            state: 'document' === tab ? 'now' : 'done',
            dirty,
            onClick: () => this.setState({ tab: 'document' }),
        },
        ...(this.props.entity?.id
            ? ([
                  {
                      label: <PaloDocumentChainTabName />,
                      state: 'chain' === tab ? 'now' : 'todo',
                      onClick: () => this.setState({ tab: 'chain' }),
                  },
              ] as PaloStep[])
            : []),
    ];

    /** Who, from where, how much — readable before anything is opened (#2259). */
    private getMeta(values: SalesOrderDetailFormValues) {
        const items = (values.positions ?? []).flatMap(position => position.items ?? []);
        const suppliers = new Set(
            items.map(item => item.supplierAccountId ?? item.supplierAccountName).filter(Boolean)
        );
        const total = items.reduce((sum, item) => sum + FinancialCalculations.getNetTotal(item), 0);
        // A single inquiry carries no prices yet, and its items hold 0 rather
        // than none: no value rather than 0,00, as the list decides (R-183,
        // R-174).
        const priced = items.some(item => !!item.price);
        const currency = this.props.settings.currency;

        return [
            ...(values.customerAccount?.name ? [{ label: '', value: values.customerAccount.name }] : []),
            ...(this.props.entity?.type
                ? [
                      {
                          label: '',
                          value: (
                              <FormattedMessage
                                  id={`app.orderType.${this.props.entity.type
                                      .replace('TYPE_', '')
                                      .replace(/_/g, '')
                                      .toLowerCase()}`}
                              />
                          ),
                      },
                  ]
                : []),
            {
                label: <FormattedMessage id="app.salesOrders.detail.meta.positions" />,
                value: items.length,
            },
            {
                label: <FormattedMessage id="app.salesOrders.detail.meta.suppliers" />,
                value: suppliers.size,
            },
            {
                label: <FormattedMessage id="app.salesOrders.detail.meta.total" />,
                value: priced ? (
                    <>
                        <FormattedNumber value={total} minimumFractionDigits={2} maximumFractionDigits={2} />{' '}
                        {paloCurrencySign(currency)}
                    </>
                ) : (
                    '–'
                ),
            },
        ];
    }

    public render() {
        const { entity, user } = this.props;
        const { unlocked, ctaCanComplete, warningDialogVisible, convertReceiptVisible } = this.state;

        // A cart waiting for approval and one the member declined are the
        // member's to decide; POOLALPIN only reads them (R-165). An approved one
        // is through like a completed one.
        const status = entity?.status as DocumentStatus | SalesOrderStatus | undefined;
        const isMembersDecision = SalesOrderStatus.CartPending === status || SalesOrderStatus.Declined === status;
        const isCompleted = DocumentStatus.Completed === status || SalesOrderStatus.CompletedWithApproval === status;
        const readonly = !!entity && (isMembersDecision || (isCompleted && !unlocked));
        const canUnlock =
            hasRole(UserRole.SuperAdmin, user) &&
            entity &&
            entity.status !== DocumentStatus.Created &&
            !isMembersDecision;
        // Nothing to advance or save on the member's decision: it reads as settled.
        const phase = isMembersDecision ? 'done' : paloDocumentPhase(entity?.status, { saved: !!entity?.id, unlocked });
        // There is no chain tab on an unsaved order, so it cannot be the open one.
        const tab: PaloSalesOrderTab = entity?.id ? this.state.tab : 'document';

        /*
         * Only the step that is due stands in the head. Making the follow-up
         * document is that step once the order is through; before then it was a
         * greyed-out button beside the one the user actually wanted, and the
         * head is meant to carry one action, not a row of them (#2206).
         */
        const actions = (
            <>
                {isCompleted && (
                    <PaloFlowAction phase={phase} kind="followUp" onClick={this.handleConvertReceiptOpen}>
                        <FormattedMessage id="app.menu.action.transition" />
                    </PaloFlowAction>
                )}
                {!isCompleted && !isMembersDecision && (
                    <PaloFlowAction
                        phase={phase}
                        kind="advance"
                        disabled={!ctaCanComplete}
                        onClick={this.handleSaveAndComplete}
                        blockedReason={<FormattedMessage id="app.palo.action.blocked.incomplete" />}
                    >
                        <FormattedMessage id="app.common.complete" />
                    </PaloFlowAction>
                )}
            </>
        );

        /** Locking and deleting: two clicks, never the one meant for saving. */
        const menu = (
            <PaloOverflowMenu
                actions={[
                    ...(isCompleted
                        ? []
                        : [
                              {
                                  label: <FormattedMessage id="app.menu.action.transition" />,
                                  disabled: true,
                                  onClick: this.handleConvertReceiptOpen,
                              },
                          ]),
                    ...(canUnlock
                        ? [
                              {
                                  label: <FormattedMessage id={unlocked ? 'app.label.lock' : 'app.label.unlock'} />,
                                  onClick: this.handleUnlockedChange,
                              },
                          ]
                        : []),
                    {
                        label: <FormattedMessage id="app.common.previewPrint" />,
                        disabled: !entity?.id,
                        onClick: () => this.setState({ previewVisible: true }),
                    },
                    {
                        label: <FormattedMessage id="app.common.delete" />,
                        disabled: !!entity && entity.status !== DocumentStatus.Created,
                        danger: true,
                        onClick: this.handleWarningOpen,
                    },
                ]}
            />
        );

        const primaryAction = <PaloSaveAction phase={phase} onSave={this.handleManualSave} />;

        // One scheme for every Belegmaske: notices in the head, attachments at
        // the right edge without tabs, history at the foot (#2216).
        const dock = <PaloRecordDock count={this.props.attachmentCount} attachments={<PaloSalesOrderAttachments />} />;

        // Nothing to look back on before the first save.
        const history = entity?.id ? (
            <PaloHistory>
                <DetailStream documentType={DocumentType.SalesOrder} documentId={entity.id} />
            </PaloHistory>
        ) : undefined;

        return (
            <Formik
                initialValues={this.initialValues}
                onSubmit={this.handleSubmit}
                innerRef={ref => (this.formikInstance = ref)}
                validationSchema={salesOrderDetailValidationSchema}
                enableReinitialize={true}
            >
                {(formikBag: FormikProps<SalesOrderDetailFormValues>) => (
                    <PoolSpinner enabled={this.isLoading} sx={{ display: 'block', height: '100%' }}>
                        <PaloDirtyReporter />
                        <PaloSalesOrderNotesWatcher />
                        <ScrollToFieldError />
                        {/* Lends the "Belege" tab the document a follow-up is
                            made from, and the handler that makes it (#2166). */}
                        {this.props.entity && (
                            <PaloDocumentChainCreate source={this.props.entity} onConvert={this.handleTransition} />
                        )}
                        <PaloDocumentTitle typeId="app.document.salesOrder" number={entity?.number} />
                        <PaloDetailView
                            themeContent={false}
                            onBack={() => this.props.navigate(ROUTES.salesOrders)}
                            number={entity?.number}
                            title={<FormattedMessage id="app.document.salesOrder" />}
                            dirty={!readonly && formikBag.dirty}
                            status={
                                entity ? (
                                    <PaloStatusPill
                                        status={entity.status}
                                        label={<FormattedMessage id={getDocumentStatusTranslationId(entity.status)} />}
                                    />
                                ) : undefined
                            }
                            meta={this.getMeta(formikBag.values)}
                            tabs={<PaloTabs steps={this.getTabs(tab, !readonly && formikBag.dirty)} />}
                            alert={
                                <>
                                    <PaloSalesOrderApprovalNotice status={entity?.status} />
                                    <PaloDocumentNotices />
                                </>
                            }
                            breadcrumbs={[
                                {
                                    label: <FormattedMessage id="app.navigation.salesOrders" />,
                                    onClick: () => this.props.navigate(ROUTES.salesOrders),
                                },
                            ]}
                            actions={actions}
                            pager={
                                <PaloRecordPager
                                    currentId={entity?.id}
                                    onOpen={id => this.props.navigate(`${ROUTES.salesOrders}/${id}`)}
                                />
                            }
                            menu={menu}
                            preview={{
                                onOpen: () => this.setState({ previewVisible: true }),
                                disabled: !entity?.id,
                                disabledHint: <FormattedMessage id="app.common.previewAfterSave" />,
                            }}
                            primaryAction={primaryAction}
                            dock={entity?.id ? dock : undefined}
                            history={history}
                        >
                            <PaloThemeProvider>
                                {'chain' === tab ? (
                                    <PaloDocumentChain />
                                ) : (
                                    <Box>
                                        <Box sx={{ mb: '16px' }}>
                                            <PaloSalesOrderDetailCommon
                                                readonly={readonly}
                                                onCustomerSelected={this.handleQuickCustomerSelected}
                                            />
                                        </Box>

                                        <FieldArray name="positions">
                                            {helper => (
                                                <PaloFormSection
                                                    title={
                                                        <FormattedMessage id="app.salesOrders.detail.deliveryAddresses" />
                                                    }
                                                    action={<PaloDescriptionModeToggle />}
                                                    collapsible={false}
                                                >
                                                    {formikBag.values.positions.map((_, index) => (
                                                        <PaloSalesOrderDetailPosition
                                                            key={index}
                                                            index={index}
                                                            readonly={readonly}
                                                            onCustomerRemove={this.handleCustomerRemove(helper)}
                                                            focusAfterMount={this.focusPosition}
                                                        />
                                                    ))}
                                                    {this.hasError(formikBag, 'positions') && (
                                                        <FormHelperText error={true}>
                                                            {this.getError(formikBag, 'positions')}
                                                        </FormHelperText>
                                                    )}
                                                    {!readonly && (
                                                        <ButtonBase
                                                            onClick={this.handleAddPosition(helper)}
                                                            sx={{
                                                                gap: '5px',
                                                                fontSize: '12.5px',
                                                                color: tokens.color.accentHi,
                                                                '&:hover': { textDecoration: 'underline' },
                                                            }}
                                                        >
                                                            <AddCircleOutline sx={{ fontSize: 17 }} />
                                                            <FormattedMessage id="app.salesOrders.detail.addDeliveryAddress" />
                                                        </ButtonBase>
                                                    )}
                                                </PaloFormSection>
                                            )}
                                        </FieldArray>

                                        {/* Both remarks open and side by side, the rule for
                                            every document (GF 14.10.2026). The member's
                                            comes from the shop and is only read here. */}
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: '16px',
                                                mt: '8px',
                                            }}
                                        >
                                            <Box>
                                                <Box sx={{ fontSize: '12px', color: tokens.color.ink2, mb: '5px' }}>
                                                    <FormattedMessage id="app.salesOrders.detail.noteFromCustomer" />
                                                    <Box component="span" sx={{ color: tokens.color.ink3 }}>
                                                        {' · '}
                                                        <FormattedMessage id="app.salesOrders.detail.fromShop" />
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        minHeight: 56,
                                                        padding: '8px 10px',
                                                        fontSize: '12.5px',
                                                        whiteSpace: 'pre-wrap',
                                                        color: entity?.noteFromCustomer
                                                            ? tokens.color.ink
                                                            : tokens.color.ink3,
                                                        backgroundColor: tokens.color.canvas,
                                                        border: `1px solid ${tokens.color.line}`,
                                                        borderRadius: tokens.radius.small + 'px',
                                                    }}
                                                >
                                                    {entity?.noteFromCustomer || (
                                                        <FormattedMessage id="app.salesOrders.detail.noNoteFromCustomer" />
                                                    )}
                                                </Box>
                                            </Box>
                                            <PaloTextControl
                                                name="internalNote"
                                                label={<FormattedMessage id="app.header.internalNote" />}
                                                multiline
                                                minRows={2}
                                                maxRows={12}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </PaloThemeProvider>
                        </PaloDetailView>
                        <SalesOrderDetailCtaHelper onChange={this.handleCtaHelperChanged} />

                        <PaloConfirmationDialog
                            isVisible={warningDialogVisible}
                            title={<FormattedMessage id="app.common.deleteHeader" />}
                            action={<FormattedMessage id="app.common.delete" />}
                            onCancel={this.handleWarningClose}
                            onConfirm={this.handleConfirmDelete}
                        >
                            <FormattedMessage id="app.common.deleteWarning" />
                        </PaloConfirmationDialog>
                        <PaloDocumentPreview
                            url={
                                this.state.previewVisible && entity?.id
                                    ? urlJoin(API_BASE_URL, `/sales-orders/${entity.id}/pdf-sales-order`)
                                    : undefined
                            }
                            title={<FormattedMessage id="app.common.preview" />}
                            openLabel={<FormattedMessage id="app.common.openInNewTab" />}
                            closeLabel={<FormattedMessage id="app.common.close" />}
                            onClose={() => this.setState({ previewVisible: false })}
                        />
                        {entity && (
                            <ConverterReceiptModal
                                isVisible={convertReceiptVisible}
                                onClose={this.handleConvertReceiptClose}
                                source={entity}
                                onConvert={this.handleTransition}
                            />
                        )}
                    </PoolSpinner>
                )}
            </Formik>
        );
    }
}

const PaloSalesOrderDetailWithRouter = withRouter(BasePaloSalesOrderDetail);

const mapStateToProps = (state: AppState) => ({
    entity: state.salesOrders.entity,
    settings: state.settings,
    user: state.user,
    transitionsFromCount: state.transitionActivities.transitionsFromCount,
    activityCount: activitiesSelector(state).length,
    attachmentCount: state.media.flat.length,
});

const ConnectedSalesOrderDetail = connect(mapStateToProps, {
    createOrUpdateSalesOrder,
    deleteSalesOrder,
    fetchSalesOrder,
    resetSalesOrderState,
    resetUploads,
    setUploads,
    transitionSalesOrderIntoPurchaseOrders,
    transitionSalesOrderIntoInquiries,
    fetchActivities,
    resetActivities,
    fetchTransitions,
    resetTransitions,
    setDocumentType,
    resetDocumentType,
    fetchReceiptInternalNotes,
    resetReceiptInternalNotes,
})(PaloSalesOrderDetailWithRouter);

/** Carries the unsaved marker in the head and the warning before leaving. */
export const PaloSalesOrderDetail = (props: Record<string, unknown>): ReactElement => (
    <PaloDirtyProvider>
        <PaloDocumentChainProvider documentType={DocumentType.SalesOrder}>
            <ConnectedSalesOrderDetail {...props} />
        </PaloDocumentChainProvider>
    </PaloDirtyProvider>
);
